<template>
  <link rel="stylesheet" href="https://cdnjs.cloudflare.com/ajax/libs/font-awesome/5.15.4/css/all.min.css" />
  <div class="cursos-container">
    <div
      v-for="(modulo, index) in modulos"
      :key="modulo.id"
      :class="['modulo', { 'modulo--locked': modulo.bloqueado }]"
    >
      <div
        class="modulo-header"
        @click="toggleModulo(index)"
        :aria-expanded="modulo.aberto && !modulo.bloqueado"
        :aria-disabled="modulo.bloqueado"
      >
        <div class="modulo-title">
          <i class="module-icon" :class="modulo.icon"></i>
          <span>{{ modulo.titulo }}</span>
        </div>
        <div class="modulo-actions">
          <span class="badge">{{ modulo.aulas.length }} Aulas</span>
          <i :class="['fas', modulo.aberto ? 'fa-chevron-down' : 'fa-chevron-right']" class="icon-seta"></i>
        </div>
      </div>

      <div v-if="modulo.bloqueado" class="modulo-lock" aria-hidden="true">
        <i class="fas fa-lock"></i>
      </div>

      <transition
        @before-enter="beforeEnter"
        @enter="enter"
        @after-enter="afterEnter"
        @before-leave="beforeLeave"
        @leave="leave"
        @after-leave="afterLeave"
      >
        <div v-if="modulo.aberto && !modulo.bloqueado" class="modulo-body">
          <div
            v-for="(aula, idx) in modulo.aulas"
            :key="idx"
            class="aula-card"
            :class="{ 'aula-card--disabled': !canPlayLesson(aula) }"
            role="button"
            tabindex="0"
            @click="openLesson(modulo, aula)"
            @keydown.enter.prevent="openLesson(modulo, aula)"
            @keydown.space.prevent="openLesson(modulo, aula)"
          >
            <i class="fas fa-play-circle icon-play"></i>
            <div class="aula-content">
              <h3 class="aula-title">{{ aula.titulo }}</h3>
              <p v-if="aula.descricao" class="aula-descricao">{{ aula.descricao }}</p>
            </div>
            <span v-if="!canPlayLesson(aula)" class="lesson-status">Em breve</span>
          </div>
        </div>
      </transition>
    </div>

    <teleport to="body">
      <transition name="video-fade">
        <div v-if="isVideoModalOpen" class="video-overlay" @click.self="closeVideoModal">
          <div class="video-modal" role="dialog" aria-modal="true" aria-labelledby="lesson-title">
            <button class="video-close" type="button" @click="closeVideoModal" aria-label="Fechar aula">
              <i class="fas fa-times"></i>
            </button>
            <p class="video-module">{{ getModalModuleTitle() }}</p>
            <div class="video-title-row">
              <h2 id="lesson-title" class="video-title">{{ activeLesson?.titulo }}</h2>
              <div class="video-lesson-nav">
                <button
                  type="button"
                  class="lesson-nav-btn"
                  :disabled="!canGoPrevLesson()"
                  @click="goToPrevLesson"
                  :aria-label="isPrevModuleTransition() ? 'Módulo anterior' : 'Aula anterior'"
                >
                  <i :class="['fas', isPrevModuleTransition() ? 'fa-angle-double-left' : 'fa-chevron-left']"></i>
                </button>
                <button
                  type="button"
                  class="lesson-nav-btn"
                  :disabled="!canGoNextLesson()"
                  @click="goToNextLesson"
                  :aria-label="isNextModuleTransition() ? 'Próximo módulo' : 'Próxima aula'"
                >
                  <i :class="['fas', isNextModuleTransition() ? 'fa-angle-double-right' : 'fa-chevron-right']"></i>
                </button>
              </div>
            </div>
            <div class="video-frame-shell">
              <iframe
                v-if="videoSrc"
                :src="videoSrc"
                title="Player da aula"
                frameborder="0"
                allow="autoplay; fullscreen; picture-in-picture; clipboard-write; encrypted-media"
                referrerpolicy="strict-origin-when-cross-origin"
                allowfullscreen
              ></iframe>
            </div>
            <p v-if="getModalDescription(activeLesson)" class="video-description">
              {{ getModalDescription(activeLesson) }}
            </p>
            <p v-else-if="getModalDescriptionLink(activeLesson)" class="video-description">
              <a class="video-description-link" :href="getModalDescriptionLink(activeLesson)" target="_blank" rel="noopener noreferrer">
                {{ getModalDescriptionLink(activeLesson) }}
              </a>
            </p>
          </div>
        </div>
      </transition>
    </teleport>
  </div>
</template>

<script>
const lesson = (titulo, descricao, vimeoId = '') => ({ titulo, descricao, vimeoId });

export default {
  name: 'Cursos',
  data() {
    return {
      isVideoModalOpen: false,
      activeLesson: null,
      activeModuleTitle: '',
      activeModuleId: null,
      activeLessonIndex: -1,
      videoSrc: '',
      modulos: [
        {
          id: 1,
          titulo: 'Módulo 1 - Primeira Conexão: Criando sua Raiz no Sistema',
          icon: 'fas fa-cogs',
          bloqueado: false,
          aberto: false,
          aulas: [
            lesson('Seja bem-vindo!', 'Contexto inicial, introdução ao ambiente e como navegar.', '1168777387'),
            lesson('Abrindo Conta', 'Passo a passo de como criar conta na exchange.', '1168779239'),
            lesson('Depositando e transferindo para o Futures', 'Guia para realizar depósitos e transferências.', '1168338736'),
            lesson('Conexão API', 'Entenda como conectar a automação de operações futures.', '1168989977'),
          ]
        },
        {
          id: 2,
          titulo: 'Módulo 2 - Estrutura Inicial: Entendendo a Engenharia da Cópia',
          icon: 'fas fa-project-diagram',
          bloqueado: false,
          aberto: false,
          aulas: [
            lesson('Usando o sistema', 'Demonstração prática de uso do sistema.', '1172284990'),
            lesson('Entendendo uma trade', 'Explicação de uma trade aberta.', '1172396926'),
            lesson('Estratégia utilizada nas operações', 'Apresentação das estratégias operacionais.', '1172568013'),
          ]
        },
        {
          id: 3,
          titulo: 'Módulo 3 - Mentalidade de Executor: Forjando a Mente Antifrágil',
          icon: 'fas fa-brain',
          bloqueado: false,
          aberto: false,
          aulas: [
            lesson('Antifragilidade e Psicologia de Mercado', 'Desenvolvendo resiliência e perspectiva de mercado.', '1182053553'),
            lesson('Controle emocional e do ego', 'Desenvolvendo uma visão transcendental.', '1192428973'),
            lesson('Gerenciamento com R:R Real', 'Estratégias de gerenciamento de risco efetivas.', '1192909532'),
            lesson('Como evitar armadilhas institucionais', 'Identificando e evitando armadilhas no mercado.', '1192577227'),
            lesson('Estratégia da espera', 'Find > Wait > Play', '1192909533'),
          ]
        },
        {
          id: 4,
          titulo: 'Módulo 4 - Afiação Estratégica: Dominando Ferramentas Táticas',
          icon: 'fas fa-chart-line',
          bloqueado: true,
          aberto: false,
          aulas: [
            lesson('Leitura Estrutural do Gráfico', 'BOS, CHoCH, MS'),
            lesson('Ferramentas para o Caçador', 'Fib, OBs, FVGs'),
            lesson('Domínio das Zonas de Interesse', 'Premium, Discount'),
            lesson('Ajuste de Visão Multitemporal', 'HTF > LTF'),
            lesson('A Arte da Confluência', 'Integração de indicadores para análise completa.'),
          ]
        }
      ]
    };
  },
  watch: {
    '$route.query.openModule': {
      immediate: true,
      handler(value) {
        const moduleId = Number(value);
        if (!Number.isFinite(moduleId)) return;
        this.openModuleById(moduleId);
        this.clearQueryFlag('openModule');
      }
    }
  },
  mounted() {
    window.addEventListener('keydown', this.handleGlobalKeydown);
  },
  beforeUnmount() {
    window.removeEventListener('keydown', this.handleGlobalKeydown);
    document.body.style.overflow = '';
  },
  methods: {
    getModalModuleTitle() {
      const moduleTitle = String(this.activeModuleTitle || '').trim();
      const lessonDescription = String(this.activeLesson?.descricao || '').trim();
      if (!moduleTitle) return lessonDescription;
      const separatorIndex = moduleTitle.indexOf(':');
      const moduleBase = separatorIndex >= 0 ? moduleTitle.slice(0, separatorIndex).trim() : moduleTitle;
      if (!lessonDescription) return moduleTitle;
      return `${moduleBase}: ${lessonDescription}`;
    },
    getModalDescription(lessonItem) {
      const title = String(lessonItem?.titulo || '').toLowerCase();
      if (title.includes('conex')) {
        return 'Importante estar conectado como está no vídeo! Certifique-se de marcar "read-write" (IMPORTANTE)';
      }
      return '';
    },
    getModalDescriptionLink(lessonItem) {
      const title = String(lessonItem?.titulo || '').toLowerCase();
      if (title.includes('abrindo conta')) {
        return 'https://www.bybit.com/invite?ref=BNLYRE2';
      }
      return '';
    },
    openModuleById(moduleId) {
      const index = this.modulos.findIndex((modulo) => modulo.id === moduleId && !modulo.bloqueado);
      if (index < 0) return;
      this.modulos.forEach((modulo, i) => {
        modulo.aberto = i === index;
      });
    },
    clearQueryFlag(flagKey) {
      const nextQuery = { ...this.$route.query };
      if (!(flagKey in nextQuery)) return;
      delete nextQuery[flagKey];
      this.$router.replace({
        name: this.$route.name,
        params: this.$route.params,
        query: nextQuery,
        hash: this.$route.hash
      });
    },
    canPlayLesson(aula) {
      return !!String(aula?.vimeoId || '').trim();
    },
    buildVimeoEmbedUrl(vimeoId) {
      const id = String(vimeoId || '').trim();
      if (!id) return '';
      const params = new URLSearchParams({
        autoplay: '1',
        title: '0',
        byline: '0',
        portrait: '0',
        badge: '0',
        dnt: '1'
      });
      return `https://player.vimeo.com/video/${id}?${params.toString()}`;
    },
    openLesson(modulo, aula) {
      if (!this.canPlayLesson(aula) || modulo?.bloqueado) return;
      const lessonIndex = Array.isArray(modulo?.aulas) ? modulo.aulas.findIndex((item) => item === aula) : -1;
      this.activeLesson = aula;
      this.activeModuleTitle = modulo?.titulo || 'Aula';
      this.activeModuleId = modulo?.id ?? null;
      this.activeLessonIndex = lessonIndex;
      this.videoSrc = this.buildVimeoEmbedUrl(aula.vimeoId);
      this.isVideoModalOpen = true;
      document.body.style.overflow = 'hidden';
    },
    closeVideoModal() {
      this.isVideoModalOpen = false;
      this.videoSrc = '';
      this.activeLesson = null;
      this.activeModuleTitle = '';
      this.activeModuleId = null;
      this.activeLessonIndex = -1;
      document.body.style.overflow = '';
    },
    getActiveModule() {
      if (!this.activeModuleId) return null;
      return this.modulos.find((m) => m.id === this.activeModuleId) || null;
    },
    findFirstPlayableIndex(module) {
      if (!module || !Array.isArray(module.aulas)) return -1;
      for (let i = 0; i < module.aulas.length; i += 1) {
        if (this.canPlayLesson(module.aulas[i])) return i;
      }
      return -1;
    },
    findLastPlayableIndex(module) {
      if (!module || !Array.isArray(module.aulas)) return -1;
      for (let i = module.aulas.length - 1; i >= 0; i -= 1) {
        if (this.canPlayLesson(module.aulas[i])) return i;
      }
      return -1;
    },
    findPrevPlayableInPrevModules() {
      const currentIndex = this.modulos.findIndex((m) => m.id === this.activeModuleId);
      if (currentIndex < 0) return null;
      for (let i = currentIndex - 1; i >= 0; i -= 1) {
        const module = this.modulos[i];
        if (!module || module.bloqueado) continue;
        const lessonIndex = this.findLastPlayableIndex(module);
        if (lessonIndex >= 0) return { module, lessonIndex };
      }
      return null;
    },
    findNextPlayableInNextModules() {
      const currentIndex = this.modulos.findIndex((m) => m.id === this.activeModuleId);
      if (currentIndex < 0) return null;
      for (let i = currentIndex + 1; i < this.modulos.length; i += 1) {
        const module = this.modulos[i];
        if (!module || module.bloqueado) continue;
        const lessonIndex = this.findFirstPlayableIndex(module);
        if (lessonIndex >= 0) return { module, lessonIndex };
      }
      return null;
    },
    isNextModuleTransition() {
      const module = this.getActiveModule();
      if (!module || this.activeLessonIndex < 0) return false;
      if (this.findPlayableIndex(module, this.activeLessonIndex, 1) >= 0) return false;
      return !!this.findNextPlayableInNextModules();
    },
    isPrevModuleTransition() {
      const module = this.getActiveModule();
      if (!module || this.activeLessonIndex < 0) return false;
      if (this.findPlayableIndex(module, this.activeLessonIndex, -1) >= 0) return false;
      return !!this.findPrevPlayableInPrevModules();
    },
    findPlayableIndex(module, fromIndex, direction) {
      if (!module || !Array.isArray(module.aulas)) return -1;
      for (let i = fromIndex + direction; i >= 0 && i < module.aulas.length; i += direction) {
        if (this.canPlayLesson(module.aulas[i])) {
          return i;
        }
      }
      return -1;
    },
    canGoPrevLesson() {
      const module = this.getActiveModule();
      if (!module || this.activeLessonIndex < 0) return false;
      if (this.findPlayableIndex(module, this.activeLessonIndex, -1) >= 0) return true;
      return !!this.findPrevPlayableInPrevModules();
    },
    canGoNextLesson() {
      const module = this.getActiveModule();
      if (!module || this.activeLessonIndex < 0) return false;
      if (this.findPlayableIndex(module, this.activeLessonIndex, 1) >= 0) return true;
      return !!this.findNextPlayableInNextModules();
    },
    goToPrevLesson() {
      const module = this.getActiveModule();
      if (!module) return;
      const nextIndex = this.findPlayableIndex(module, this.activeLessonIndex, -1);
      if (nextIndex >= 0) {
        const lesson = module.aulas[nextIndex];
        this.activeLesson = lesson;
        this.activeLessonIndex = nextIndex;
        this.videoSrc = this.buildVimeoEmbedUrl(lesson.vimeoId);
        return;
      }
      const prevModuleTarget = this.findPrevPlayableInPrevModules();
      if (!prevModuleTarget) return;
      const { module: prevModule, lessonIndex } = prevModuleTarget;
      const lesson = prevModule.aulas[lessonIndex];
      this.activeLesson = lesson;
      this.activeModuleTitle = prevModule?.titulo || 'Aula';
      this.activeModuleId = prevModule?.id ?? null;
      this.activeLessonIndex = lessonIndex;
      this.videoSrc = this.buildVimeoEmbedUrl(lesson.vimeoId);
    },
    goToNextLesson() {
      const module = this.getActiveModule();
      if (!module) return;
      const nextIndex = this.findPlayableIndex(module, this.activeLessonIndex, 1);
      if (nextIndex >= 0) {
        const lesson = module.aulas[nextIndex];
        this.activeLesson = lesson;
        this.activeLessonIndex = nextIndex;
        this.videoSrc = this.buildVimeoEmbedUrl(lesson.vimeoId);
        return;
      }
      const nextModuleTarget = this.findNextPlayableInNextModules();
      if (!nextModuleTarget) return;
      const { module: nextModule, lessonIndex } = nextModuleTarget;
      const lesson = nextModule.aulas[lessonIndex];
      this.activeLesson = lesson;
      this.activeModuleTitle = nextModule?.titulo || 'Aula';
      this.activeModuleId = nextModule?.id ?? null;
      this.activeLessonIndex = lessonIndex;
      this.videoSrc = this.buildVimeoEmbedUrl(lesson.vimeoId);
    },
    handleGlobalKeydown(event) {
      if (event.key === 'Escape' && this.isVideoModalOpen) {
        this.closeVideoModal();
      }
    },
    toggleModulo(index) {
      if (this.modulos[index].bloqueado) return;
      this.modulos.forEach((m, i) => {
        if (i !== index) m.aberto = false;
      });
      this.modulos[index].aberto = !this.modulos[index].aberto;
    },
    beforeEnter(el) {
      el.style.maxHeight = '0';
      el.style.opacity = 0;
    },
    enter(el) {
      el.style.maxHeight = `${el.scrollHeight}px`;
      el.style.opacity = 1;
    },
    afterEnter(el) {
      el.style.maxHeight = null;
    },
    beforeLeave(el) {
      el.style.maxHeight = `${el.scrollHeight}px`;
      el.style.opacity = 1;
    },
    leave(el) {
      el.style.opacity = 0;
      setTimeout(() => {
        el.style.maxHeight = '0';
      });
    },
    afterLeave(el) {
      el.style.maxHeight = null;
    }
  }
};
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&family=Manrope:wght@500;600;700;800&family=Space+Grotesk:wght@500;600;700&display=swap');

:root {
  --border: rgba(115, 148, 240, 0.25);
  --accent-3: #9ad9ff;
  --text-color: #f3f6ff;
  --text-secondary: rgba(226, 232, 255, 0.7);
}

.cursos-container {
  max-width: 980px;
  width: 100%;
  margin: calc(var(--app-header-h) + 22px) auto 20px;
  padding: 0 var(--app-gutter);
  color: var(--text-color);
  position: relative;
  isolation: isolate;
  overflow: hidden;
}

.cursos-container::before {
  content: '';
  position: fixed;
  inset: 0;
  background:
    radial-gradient(circle at top left, rgba(78, 115, 223, 0.12), transparent 45%),
    radial-gradient(circle at top right, rgba(155, 89, 182, 0.14), transparent 40%),
    #11131a;
  z-index: 0;
  pointer-events: none;
}

.cursos-container::after {
  content: '';
  position: fixed;
  inset: 0;
  background-image:
    linear-gradient(rgba(255, 255, 255, 0.04) 1px, transparent 1px),
    linear-gradient(90deg, rgba(255, 255, 255, 0.04) 1px, transparent 1px);
  background-size: 32px 32px;
  opacity: 0.4;
  z-index: 0;
  pointer-events: none;
}

.cursos-container > * {
  position: relative;
  z-index: 1;
}

.modulo {
  background: rgba(17, 20, 32, 0.88);
  border: 1px solid var(--border);
  border-radius: 18px;
  margin-bottom: 18px;
  overflow: hidden;
  box-shadow: 0 8px 18px rgba(0, 0, 0, 0.18);
  transition: transform 0.2s ease, border-color 0.2s ease, background 0.2s ease;
}

.modulo:hover {
  transform: translateY(-2px);
  border-color: rgba(115, 148, 240, 0.38);
}

.modulo--locked {
  background: linear-gradient(145deg, rgba(8, 10, 17, 0.98), rgba(13, 16, 25, 0.96));
  border-color: rgba(92, 107, 150, 0.46);
  box-shadow:
    0 12px 26px rgba(0, 0, 0, 0.35),
    inset 0 0 0 1px rgba(90, 105, 146, 0.24);
  position: relative;
}

.modulo--locked::before {
  content: '';
  position: absolute;
  inset: 0;
  background:
    linear-gradient(180deg, rgba(6, 9, 15, 0.68), rgba(6, 9, 15, 0.52)),
    repeating-linear-gradient(
      -55deg,
      rgba(130, 146, 198, 0.08) 0 9px,
      rgba(30, 37, 59, 0.06) 9px 18px
    );
  pointer-events: none;
  z-index: 1;
}

.modulo--locked > * {
  position: relative;
  z-index: 2;
}

.modulo--locked:hover {
  transform: none;
}

.modulo--locked .modulo-header {
  cursor: not-allowed;
  background: rgba(10, 14, 24, 0.72);
}

.modulo--locked .modulo-title {
  color: rgba(212, 220, 247, 0.7);
}

.modulo--locked .module-icon {
  color: rgba(188, 201, 241, 0.66);
}

.modulo--locked .badge {
  background: linear-gradient(120deg, rgba(72, 84, 122, 0.88), rgba(57, 68, 104, 0.88));
  color: rgba(225, 232, 255, 0.82);
  box-shadow: inset 0 0 0 1px rgba(132, 148, 195, 0.24);
}

.modulo--locked .icon-seta {
  color: rgba(178, 191, 230, 0.62);
}

.modulo-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
  padding: 18px 20px;
  background: rgba(20, 26, 38, 0.6);
  cursor: pointer;
  position: relative;
  overflow: hidden;
}

.modulo-header::after {
  content: '';
  position: absolute;
  left: 0;
  bottom: 0;
  width: 0;
  height: 2px;
  background: linear-gradient(90deg, rgba(78, 115, 223, 0.95), rgba(155, 89, 182, 0.92));
  transition: width 0.25s ease;
}

.modulo:not(.modulo--locked):hover .modulo-header::after {
  width: 100%;
}

.modulo--locked .modulo-header::after {
  width: 0;
}

.modulo-title {
  display: flex;
  align-items: center;
  gap: 12px;
  font-size: 1.05rem;
  font-weight: 700;
}

.modulo-actions {
  display: inline-flex;
  align-items: center;
  gap: 10px;
}

.module-icon {
  font-size: 1.35rem;
  color: var(--accent-3);
}

.badge {
  background: linear-gradient(120deg, rgba(78, 115, 223, 0.9), rgba(155, 89, 182, 0.9));
  color: #fff;
  padding: 6px 12px;
  border-radius: 999px;
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
}

.icon-seta {
  color: var(--accent-3);
}

.fa-chevron-down {
  transform: rotate(180deg);
}

.modulo-lock {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 46px;
  height: 46px;
  border-radius: 999px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background:
    linear-gradient(145deg, rgba(17, 23, 36, 0.96), rgba(29, 36, 52, 0.96)) padding-box,
    linear-gradient(135deg, rgba(146, 163, 212, 0.66), rgba(104, 120, 167, 0.58)) border-box;
  border: 1px solid transparent;
  color: #dce5ff;
  box-shadow:
    0 0 0 8px rgba(8, 12, 20, 0.5),
    0 14px 30px rgba(0, 0, 0, 0.48);
  pointer-events: none;
  z-index: 3;
}

.modulo-lock i {
  font-size: 1.08rem;
}

.modulo-body {
  overflow: hidden;
  padding: 12px 16px 18px;
  display: grid;
  gap: 10px;
  transition: max-height 0.3s ease, opacity 0.3s ease;
}

.aula-card {
  display: inline-flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  background: rgba(20, 26, 38, 0.6);
  border: 1px solid rgba(115, 148, 240, 0.25);
  border-radius: 999px;
  padding: 10px 18px;
  transition: transform 0.25s ease, box-shadow 0.25s ease, border-color 0.25s ease, background 0.25s ease;
}

.aula-card:hover {
  transform: translateY(-2px) scale(1.005);
  cursor: pointer;
  border-color: rgba(115, 148, 240, 0.55);
  background: linear-gradient(120deg, rgba(78, 115, 223, 0.16), rgba(155, 89, 182, 0.14)), rgba(20, 26, 38, 0.9);
  box-shadow: 0 14px 32px rgba(5, 8, 15, 0.48), 0 0 0 1px rgba(115, 148, 240, 0.35);
}

.aula-card--disabled {
  cursor: not-allowed;
  opacity: 0.86;
}

.aula-card--disabled:hover {
  transform: none;
  border-color: rgba(115, 148, 240, 0.25);
  background: rgba(20, 26, 38, 0.6);
  box-shadow: none;
}

.icon-play {
  font-size: 1.05rem;
  color: var(--accent-3);
  width: 30px;
  height: 30px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.aula-content {
  display: flex;
  flex-direction: column;
}

.aula-title {
  font-weight: 600;
  margin-bottom: 4px;
  font-family: 'Space Grotesk', 'Manrope', 'Inter', sans-serif;
}

.aula-descricao {
  font-size: 0.9rem;
  color: var(--text-secondary);
  margin: 0;
  font-family: 'Space Grotesk', 'Manrope', 'Inter', sans-serif;
}

.lesson-status {
  margin-left: auto;
  padding: 5px 10px;
  border-radius: 999px;
  font-size: 0.72rem;
  font-weight: 700;
  text-transform: uppercase;
  color: rgba(225, 231, 255, 0.88);
  background: rgba(63, 76, 112, 0.45);
  border: 1px solid rgba(132, 145, 184, 0.42);
}

.video-overlay {
  position: fixed;
  inset: 0;
  z-index: 2500;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  background:
    radial-gradient(circle at 14% 12%, rgba(78, 115, 223, 0.32), transparent 42%),
    radial-gradient(circle at 86% 82%, rgba(155, 89, 182, 0.28), transparent 44%),
    radial-gradient(circle at 50% 100%, rgba(45, 183, 255, 0.12), transparent 58%),
    rgba(6, 8, 14, 0.84);
  backdrop-filter: blur(12px) saturate(115%);
}

.video-modal {
  position: relative;
  width: min(1160px, 100%);
  max-height: calc(100dvh - 24px);
  border-radius: 20px;
  border: 1px solid transparent;
  background:
    linear-gradient(160deg, rgba(10, 17, 34, 0.94), rgba(8, 14, 28, 0.96)) padding-box,
    linear-gradient(125deg, rgba(93, 141, 255, 0.8), rgba(103, 202, 255, 0.58), rgba(189, 114, 255, 0.8)) border-box;
  padding: 18px 18px 14px;
  box-shadow:
    0 28px 58px rgba(0, 0, 0, 0.55),
    0 0 0 1px rgba(164, 187, 255, 0.16) inset,
    0 0 34px rgba(73, 118, 255, 0.16);
  overflow: auto;
  font-family: 'Space Grotesk', 'Manrope', 'Inter', sans-serif;
}

.video-modal::before {
  content: '';
  position: absolute;
  inset: 0;
  border-radius: inherit;
  background:
    radial-gradient(circle at 10% 0, rgba(91, 143, 255, 0.16), transparent 44%),
    radial-gradient(circle at 88% 12%, rgba(186, 119, 255, 0.12), transparent 44%);
  pointer-events: none;
}

.video-close {
  position: absolute;
  top: 12px;
  right: 12px;
  width: 32px;
  height: 32px;
  padding: 0;
  border-radius: 999px;
  border: 1px solid rgba(139, 167, 255, 0.46);
  background:
    linear-gradient(165deg, rgba(18, 25, 43, 0.8), rgba(12, 17, 30, 0.82)) padding-box,
    linear-gradient(135deg, rgba(78, 115, 223, 0.7), rgba(155, 89, 182, 0.68)) border-box;
  -webkit-appearance: none;
  appearance: none;
  border-radius: 999px;
  color: #fff;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  line-height: 1;
  z-index: 2;
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.35);
  transition: transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease;
}

.video-close i {
  font-size: 0.82rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.video-close:hover {
  transform: translateY(-1px);
  border-color: rgba(176, 197, 255, 0.8);
  box-shadow: 0 10px 24px rgba(8, 13, 28, 0.45);
}

.video-close:focus-visible {
  outline: 2px solid rgba(143, 181, 255, 0.85);
  outline-offset: 2px;
}

.video-module {
  margin: 0 0 8px;
  color: rgba(208, 222, 255, 0.84);
  font-size: 0.8rem;
  text-transform: uppercase;
  letter-spacing: 0.09em;
  padding-right: 44px;
  position: relative;
  z-index: 1;
  font-family: 'Space Grotesk', 'Manrope', 'Inter', sans-serif;
}

.video-title {
  margin: 0;
  color: #f3f6ff;
  font-size: clamp(1.12rem, 2.4vw, 1.6rem);
  font-weight: 800;
  padding-right: 10px;
  line-height: 1.2;
  position: relative;
  z-index: 1;
  font-family: 'Space Grotesk', 'Manrope', 'Inter', sans-serif;
}

.video-title-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin: 0 46px 10px 0;
  position: relative;
  z-index: 1;
}

.video-lesson-nav {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  position: relative;
  z-index: 1;
  flex: 0 0 auto;
}

.lesson-nav-btn {
  width: 30px;
  height: 30px;
  border: 1px solid rgba(144, 164, 212, 0.5);
  border-radius: 3px;
  background: linear-gradient(145deg, rgba(18, 26, 43, 0.92), rgba(10, 16, 29, 0.92));
  color: rgba(230, 238, 255, 0.95);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: transform 0.18s ease, border-color 0.18s ease, background 0.18s ease, opacity 0.18s ease;
}

.lesson-nav-btn:hover:not(:disabled) {
  transform: translateY(-1px);
  border-color: rgba(178, 198, 248, 0.78);
  background: linear-gradient(145deg, rgba(28, 39, 63, 0.95), rgba(12, 19, 35, 0.94));
}

.lesson-nav-btn:disabled {
  opacity: 0.42;
  cursor: not-allowed;
}

.lesson-nav-btn i {
  font-size: 0.78rem;
}

.video-frame-shell {
  width: 100%;
  aspect-ratio: 16 / 9;
  max-height: min(72dvh, calc(100dvh - 190px));
  border-radius: 16px;
  overflow: hidden;
  background: #05070d;
  border: 1px solid rgba(102, 144, 255, 0.65);
  box-shadow:
    inset 0 0 0 1px rgba(183, 204, 255, 0.08),
    0 12px 34px rgba(5, 8, 18, 0.55);
}

.video-frame-shell iframe {
  width: 100%;
  height: 100%;
  display: block;
  border-radius: 16px;
  background: #05070d;
}

.video-description {
  margin: 12px 2px 0;
  color: rgba(224, 233, 255, 0.84);
  font-size: 0.92rem;
  line-height: 1.45;
  position: relative;
  z-index: 1;
  font-family: 'Space Grotesk', 'Manrope', 'Inter', sans-serif;
}

.video-description-link {
  color: #9fd3ff;
  text-decoration: none;
  border-bottom: 1px dashed rgba(159, 211, 255, 0.52);
}

.video-description-link:hover {
  color: #d5edff;
  border-bottom-color: rgba(213, 237, 255, 0.76);
}

.video-fade-enter-active,
.video-fade-leave-active {
  transition: opacity 0.2s ease;
}

.video-fade-enter-from,
.video-fade-leave-to {
  opacity: 0;
}

@media (max-width: 767px) {
  .cursos-container {
    margin: calc(var(--app-header-h) + 14px) auto 12px;
  }

  .modulo-header {
    flex-direction: column;
    align-items: flex-start;
  }

  .modulo-actions {
    width: 100%;
    justify-content: space-between;
  }

  .modulo-body {
    padding: 10px 10px 14px;
    gap: 8px;
  }

  .aula-card {
    flex-direction: row;
    align-items: flex-start;
    gap: 10px;
    border-radius: 14px;
    padding: 10px 12px;
  }

  .lesson-status {
    margin-left: 0;
    margin-top: 8px;
  }

  .video-overlay {
    padding: 10px;
  }

  .video-modal {
    padding: 14px 12px 12px;
    border-radius: 14px;
    max-height: calc(100dvh - 12px);
  }

  .video-title {
    font-size: 1rem;
  }

  .video-title-row {
    margin-right: 36px;
    align-items: flex-start;
  }

}
</style>
